// Zapsat úraz: průvodce v 5 krocích pro telefon (15. 10. 2026, schválený mockup).
// 1 Kdo a kdy (podle typu: dnešní osobní tréninky, účastnice bootcampu, nebo kdokoli),
// 2 Co se stalo, 3 Zranění a první pomoc, 4 Svědci a opatření, 5 Kontrola a podpis přes SMS.
// Každé „Další“ uloží koncept (/api/uraz, akce ulozit), takže se nic neztratí.
// ?id=X otevře rozepsaný záznam k dokončení nebo úpravě.
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Bandage, Search, Plus, Trash2, Camera, MessageSquare, Check } from "lucide-react";
import GradientHeader from "../../components/GradientHeader.jsx";
import { Stranka } from "../../components/ui.jsx";
import { toast } from "../../components/Toast.jsx";
import { apiFetch } from "../../lib/api.js";
import { TYPY_TRENINKU, ZRANENY_TYP, CASTI_TELA, STRANY, DRUHY, ZACHRANKA, LEKAR, POPISEK, Cipy, zaklad, fmtDatum } from "./urazUi.jsx";

const KROKY = ["Kdo a kdy", "Co se stalo", "Zranění a první pomoc", "Svědci a opatření", "Kontrola a podpis"];
const PRAZDNY = {
  typ_treninku: null, booking_id: null, bootcamp_lekce_id: null, bootcamp_nazev: null, zraneny_typ: null, zraneny_klient_id: null,
  zraneny_trener_id: null, zraneny_ucastnice_id: null, zraneny_jmeno: "", zraneny_telefon: "", datum: "", cas: "", misto: "",
  trener_id: null, trener_jmeno: "", u_toho: "", cinnost: "", pricina: "", zraneni_druh: null, cast_tela: null, strana: null,
  zraneni_popis: "", prvni_pomoc: "", zachranka: null, lekar: null, svedci: [], opatreni: "",
};
const dnes = () => new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Prague" });
const ted = () => new Date().toLocaleTimeString("sv-SE", { timeZone: "Europe/Prague", hour: "2-digit", minute: "2-digit" });
const tel9 = (t) => { const d = String(t || "").replace(/\D/g, ""); return (d.length > 9 ? d.slice(-9) : d).length === 9; };

// Fotka se před nahráním zmenší (delší strana 1600 px, JPEG 80 %), z telefonu má jinak 3 až 8 MB.
// PDF a formáty, které prohlížeč neumí vykreslit (HEIC mimo Safari), jdou beze změny.
async function zmensiFotku(f) {
  if (!/^image\/(jpeg|png|webp|heic|heif)$/.test(f.type)) return { dataUrl: await cti(f), name: f.name };
  try {
    const url = URL.createObjectURL(f);
    const img = await new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = url; });
    const k = Math.min(1, 1600 / Math.max(img.naturalWidth, img.naturalHeight));
    const c = document.createElement("canvas");
    c.width = Math.round(img.naturalWidth * k); c.height = Math.round(img.naturalHeight * k);
    c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
    URL.revokeObjectURL(url);
    return { dataUrl: c.toDataURL("image/jpeg", 0.8), name: f.name.replace(/\.[^.]+$/, "") + ".jpg" };
  } catch { return { dataUrl: await cti(f), name: f.name }; }
}
const cti = (f) => new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(f); });

async function post(body) {
  const r = await apiFetch("/api/uraz", { method: "POST", body: JSON.stringify(body) });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.error || `Chyba ${r.status}`);
  return j;
}

function Pole({ label, povinne, children }) {
  return <label className="block"><span className={POPISEK}>{label}{povinne ? " *" : ""}</span>{children}</label>;
}

function Vyber({ polozky, vybranaKey, onVyber, prazdne }) {
  if (!polozky.length) return <div className="rounded-xl border border-dashed border-slate-200 px-3 py-3 text-sm text-slate-500">{prazdne}</div>;
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-3">
      {polozky.map((p, i) => {
        const zap = vybranaKey === p.key;
        return (
          <button key={p.key} type="button" onClick={() => onVyber(p)} aria-pressed={zap}
            className={`flex w-full items-center gap-3 py-2.5 text-left ${i ? "border-t border-slate-100" : ""}`} style={{ minHeight: 48 }}>
            <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 ${zap ? "border-brand bg-brand text-white" : "border-slate-300 bg-white"}`}>{zap && <Check className="h-3.5 w-3.5" />}</span>
            <span className="min-w-0 flex-1"><span className="block text-[15px] font-semibold text-slate-900 demo-sensitive">{p.nazev}</span>{p.pod && <span className="block text-[13px] text-slate-500">{p.pod}</span>}</span>
          </button>
        );
      })}
    </div>
  );
}

function Hledani({ onVyber, jenTyp }) {
  const [q, setQ] = useState("");
  const [vys, setVys] = useState([]);
  useEffect(() => {
    if (q.trim().length < 2) { setVys([]); return undefined; }
    const t = setTimeout(async () => {
      try {
        const r = await apiFetch(`/api/uraz?akce=hledat&q=${encodeURIComponent(q.trim())}`);
        const j = await r.json();
        setVys((j.vysledky || []).filter((v) => !jenTyp || v.typ === jenTyp));
      } catch { setVys([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [q, jenTyp]);
  return (
    <div className="space-y-2">
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <input className="field w-full pl-9" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Hledat podle jména nebo telefonu" aria-label="Hledat" />
      </div>
      {vys.length > 0 && (
        <Vyber polozky={vys.map((v) => ({ key: `${v.typ}:${v.id}`, nazev: v.jmeno, pod: v.typ === "trener" ? "trenér" : "klientka", v }))}
          onVyber={(p) => { onVyber(p.v); setQ(""); setVys([]); }} prazdne="" />
      )}
    </div>
  );
}

export default function UrazNovy() {
  const { pathname } = useLocation();
  const base = zaklad(pathname);
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [id, setId] = useState(params.get("id") ? Number(params.get("id")) : null);
  const [z, setZ] = useState({ ...PRAZDNY, datum: dnes(), cas: ted() });
  const [krok, setKrok] = useState(0);
  const [moz, setMoz] = useState(null);
  const [nacteno, setNacteno] = useState(!params.get("id"));
  const [busy, setBusy] = useState(false);
  const [chyba, setChyba] = useState("");
  const [podpis, setPodpis] = useState(null); // { kod_odeslan, telefon_konec } | { bez_telefonu }
  const [kod, setKod] = useState("");
  const [prilohy, setPrilohy] = useState([]);
  const [stav, setStav] = useState("koncept");
  const set = (k, v) => setZ((s) => ({ ...s, [k]: v }));

  useEffect(() => {
    apiFetch("/api/uraz?akce=moznosti").then((r) => r.json()).then(setMoz).catch(() => setMoz({ treninky: [], lekce: [], ja: {} }));
  }, []);
  useEffect(() => {
    if (!id || nacteno) return;
    apiFetch(`/api/uraz?id=${id}`).then((r) => r.json()).then((j) => {
      if (!j.zaznam) { setChyba(j.error || "Záznam nenalezen."); return; }
      if (!j.prava?.upravit) { navigate(`${base}/${id}`, { replace: true }); return; }
      setStav(j.zaznam.stav);
      const n = { ...PRAZDNY };
      for (const k of Object.keys(PRAZDNY)) if (j.zaznam[k] !== undefined && j.zaznam[k] !== null) n[k] = j.zaznam[k];
      setZ(n); setPrilohy(j.zaznam.prilohy || []); setNacteno(true);
    }).catch(() => setChyba("Záznam se nepodařilo načíst."));
  }, [id, nacteno, base, navigate]);

  const lekce = moz?.lekce || [];
  const vybranaLekce = lekce.find((l) => l.id === Number(z.bootcamp_lekce_id)) || null;

  function zvolTyp(t) {
    setZ((s) => ({ ...s, typ_treninku: t, booking_id: null, bootcamp_lekce_id: null, bootcamp_nazev: null, zraneny_ucastnice_id: null,
      zraneny_typ: t === "osobni" || t === "bootcamp" ? "klientka" : s.zraneny_typ,
      trener_id: t === "osobni" ? (moz?.ja?.trener_id || s.trener_id) : s.trener_id,
      trener_jmeno: t === "osobni" ? (moz?.ja?.jmeno || s.trener_jmeno) : s.trener_jmeno }));
  }
  const zvolOsobu = (o) => setZ((s) => ({ ...s, zraneny_typ: o.typ === "trener" ? "trener" : "klientka", zraneny_klient_id: o.typ === "trener" ? null : o.id,
    zraneny_trener_id: o.typ === "trener" ? o.id : null, zraneny_jmeno: o.jmeno, zraneny_telefon: o.telefon || s.zraneny_telefon }));

  function chybyKroku(k) {
    const ch = [];
    if (k === 0) {
      if (!z.typ_treninku) ch.push("kde se to stalo");
      if (!String(z.zraneny_jmeno).trim()) ch.push("zraněný");
      if (!tel9(z.zraneny_telefon)) ch.push("telefon zraněného (přijde mu SMS k potvrzení)");
      if (!z.datum || !z.cas) ch.push("datum a čas");
      if (!String(z.misto).trim()) ch.push("místo");
    }
    if (k === 1) { if (!String(z.cinnost).trim()) ch.push("co se dělo"); if (!String(z.pricina).trim()) ch.push("příčina a okolnosti"); }
    if (k === 2) {
      if (!z.cast_tela) ch.push("část těla"); if (!z.zraneni_druh) ch.push("druh zranění");
      if (!String(z.prvni_pomoc).trim()) ch.push("první pomoc"); if (!z.zachranka) ch.push("záchranka"); if (!z.lekar) ch.push("lékař");
    }
    if (k === 3) (z.svedci || []).forEach((s, i) => {
      if (!String(s.jmeno || "").trim()) ch.push(`jméno svědka ${i + 1}`);
      if (!tel9(s.telefon) && !/@/.test(String(s.email || ""))) ch.push(`telefon nebo e-mail svědka ${i + 1}`);
    });
    return ch;
  }

  async function ulozit() {
    const j = await post({ akce: "ulozit", id, ...z });
    if (!id && j.zaznam?.id) { setId(j.zaznam.id); setParams({ id: String(j.zaznam.id) }, { replace: true }); }
    setPrilohy(j.zaznam?.prilohy || []);
    return j.zaznam;
  }
  async function dalsi() {
    const ch = chybyKroku(krok);
    if (ch.length) { setChyba(`Doplň prosím: ${ch.join(", ")}.`); return; }
    setChyba(""); setBusy(true);
    try { await ulozit(); setKrok((k) => Math.min(k + 1, KROKY.length - 1)); window.scrollTo(0, 0); }
    catch (e) { setChyba(e.message); } finally { setBusy(false); }
  }
  function zpet() { setChyba(""); if (krok === 0) navigate(id ? `${base}/${id}` : base); else { setKrok((k) => k - 1); window.scrollTo(0, 0); } }

  async function podepsatStart() {
    setChyba(""); setBusy(true);
    try { await ulozit(); const j = await post({ akce: "podpis_start", id }); setPodpis(j); }
    catch (e) { setChyba(e.message); } finally { setBusy(false); }
  }
  async function podepsatOverit() {
    setChyba(""); setBusy(true);
    try {
      const j = await post({ akce: "podpis_overit", id, kod });
      toast(j.sms?.ok === false ? `Podepsáno. SMS zraněnému se nepodařilo poslat: ${j.sms.chyba}` : "Podepsáno. Zraněnému odešla SMS k potvrzení.", j.sms?.ok === false ? "warning" : "success");
      navigate(`${base}/${id}`, { replace: true });
    } catch (e) { setChyba(e.message); } finally { setBusy(false); }
  }
  async function nahrat(e) {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    if (f.type === "application/pdf" && f.size > 5 * 1024 * 1024) { setChyba("PDF je větší než 5 MB."); return; }
    setBusy(true); setChyba("");
    try {
      const zid = id || (await ulozit())?.id;
      const { dataUrl, name } = await zmensiFotku(f);
      if (dataUrl.length > 6.8 * 1024 * 1024) throw new Error("Soubor je i po zmenšení větší než 5 MB.");
      const j = await post({ akce: "priloha_nahrat", id: zid, filename: name, dataUrl });
      setPrilohy(j.zaznam?.prilohy || []);
    } catch (er) { setChyba(er.message); } finally { setBusy(false); }
  }

  const treninky = useMemo(() => (moz?.treninky || []).map((t) => ({ key: t.booking_id, nazev: t.klient_name, pod: `${fmtDatum(t.datum)} ${String(t.cas || "").slice(0, 5)}, ${t.lokalita || ""}`, t })), [moz]);

  if (!nacteno) return <Stranka sirka="uzka"><div className="h-24 animate-pulse rounded-2xl bg-brand-light" />{chyba && <div className="text-sm text-rose-700">{chyba}</div>}</Stranka>;

  const krok1 = (
    <div className="glass space-y-4 p-4">
      <div><span className={POPISEK}>Kde se to stalo *</span><Cipy nazev="Kde se to stalo" volby={TYPY_TRENINKU} hodnota={z.typ_treninku} onChange={zvolTyp} /></div>

      {z.typ_treninku === "osobni" && (<>
        <div><span className={POPISEK}>Zraněná, tvoje dnešní a včerejší tréninky</span>
          <Vyber polozky={treninky} vybranaKey={z.booking_id} prazdne="Dnes ani včera nemáš v kalendáři žádný trénink. Dohledej klientku níž."
            onVyber={(p) => setZ((s) => ({ ...s, booking_id: p.t.booking_id, zraneny_typ: "klientka", zraneny_klient_id: p.t.klient_id, zraneny_trener_id: null,
              zraneny_jmeno: p.t.klient_name, zraneny_telefon: p.t.telefon || s.zraneny_telefon, datum: p.t.datum, cas: String(p.t.cas || "").slice(0, 5), misto: s.misto || p.t.lokalita || "" }))} />
        </div>
        <div><span className={POPISEK}>Nebo dohledej klientku</span><Hledani jenTyp="klientka" onVyber={(o) => { zvolOsobu(o); set("booking_id", null); }} /></div>
      </>)}

      {z.typ_treninku === "bootcamp" && (<>
        <div><span className={POPISEK}>Lekce bootcampu *</span>
          <Vyber polozky={lekce.map((l) => ({ key: l.id, nazev: `${l.nazev}`, pod: `${fmtDatum(l.datum)} ${l.cas_od || ""}${l.misto ? `, ${l.misto}` : ""}`, l }))}
            vybranaKey={Number(z.bootcamp_lekce_id) || null} prazdne="Za poslední 3 dny tu není žádná tvoje lekce."
            onVyber={(p) => setZ((s) => ({ ...s, bootcamp_lekce_id: p.l.id, bootcamp_nazev: p.l.nazev, datum: p.l.datum, cas: String(p.l.cas_od || "").slice(0, 5) || s.cas,
              misto: s.misto || p.l.misto || "", trener_id: p.l.trener_id || s.trener_id, trener_jmeno: p.l.trener || s.trener_jmeno, zraneny_ucastnice_id: null }))} />
        </div>
        {vybranaLekce && (
          <div><span className={POPISEK}>Zraněná *</span>
            <Vyber polozky={(vybranaLekce.ucastnice || []).map((u) => ({ key: u.id, nazev: u.jmeno, u }))} vybranaKey={Number(z.zraneny_ucastnice_id) || null}
              prazdne="Na lekci není nikdo přihlášený."
              onVyber={(p) => setZ((s) => ({ ...s, zraneny_ucastnice_id: p.u.id, zraneny_typ: "klientka", zraneny_klient_id: p.u.klient_id || null, zraneny_trener_id: null,
                zraneny_jmeno: p.u.jmeno, zraneny_telefon: p.u.telefon || s.zraneny_telefon }))} />
          </div>
        )}
      </>)}

      {(z.typ_treninku === "samostatne" || z.typ_treninku === "mimo") && (<>
        <div><span className={POPISEK}>Kdo se zranil *</span>
          <Cipy nazev="Kdo se zranil" volby={ZRANENY_TYP} hodnota={z.zraneny_typ}
            onChange={(v) => setZ((s) => ({ ...s, zraneny_typ: v, zraneny_klient_id: null, zraneny_trener_id: null, zraneny_jmeno: "", zraneny_telefon: "" }))} />
        </div>
        {(z.zraneny_typ === "klientka" || z.zraneny_typ === "trener") && !z.zraneny_jmeno && (
          <div><span className={POPISEK}>{z.zraneny_typ === "trener" ? "Dohledej trenéra" : "Dohledej klientku"}</span><Hledani jenTyp={z.zraneny_typ} onVyber={zvolOsobu} /></div>
        )}
        <Pole label="Kdo byl u toho"><input className="field w-full" value={z.u_toho || ""} onChange={(e) => set("u_toho", e.target.value)} placeholder="Jméno, případně kdo zapisuje" /></Pole>
      </>)}

      {z.typ_treninku && (<>
        <Pole label="Jméno a příjmení zraněného" povinne>
          <input className="field w-full" value={z.zraneny_jmeno || ""} onChange={(e) => set("zraneny_jmeno", e.target.value)} />
        </Pole>
        <Pole label="Telefon zraněného" povinne>
          <input className="field w-full" inputMode="tel" value={z.zraneny_telefon || ""} onChange={(e) => set("zraneny_telefon", e.target.value)} placeholder="Přijde mu SMS k potvrzení" />
        </Pole>
        <div className="grid grid-cols-2 gap-3">
          <Pole label="Datum úrazu" povinne><input type="date" className="field w-full" value={z.datum || ""} max={dnes()} onChange={(e) => set("datum", e.target.value)} /></Pole>
          <Pole label="Čas" povinne><input type="time" className="field w-full" value={z.cas || ""} onChange={(e) => set("cas", e.target.value)} /></Pole>
        </div>
        <Pole label="Místo (centrum, zóna, stroj)" povinne><input className="field w-full" value={z.misto || ""} onChange={(e) => set("misto", e.target.value)} placeholder="Např. VT GYM, zóna volných vah" /></Pole>
      </>)}
    </div>
  );

  const krok2 = (
    <div className="glass space-y-4 p-4">
      <Pole label="Co se dělo: cvičení, činnost, náčiní a zátěž" povinne>
        <textarea className="field w-full" rows={3} value={z.cinnost || ""} onChange={(e) => set("cinnost", e.target.value)} placeholder="Např. rumunský mrtvý tah, jednoruční činky 2× 12 kg, 3. série" />
      </Pole>
      <Pole label="Příčina a okolnosti" povinne>
        <textarea className="field w-full" rows={3} value={z.pricina || ""} onChange={(e) => set("pricina", e.target.value)} placeholder="Např. únava, špatná technika, porucha stroje, kluzká podlaha" />
      </Pole>
    </div>
  );

  const krok3 = (
    <div className="glass space-y-4 p-4">
      <div><span className={POPISEK}>Postižená část těla *</span><Cipy nazev="Část těla" volby={CASTI_TELA} hodnota={z.cast_tela} onChange={(v) => set("cast_tela", v)} /></div>
      <div><span className={POPISEK}>Strana</span><Cipy nazev="Strana" volby={STRANY} hodnota={z.strana} onChange={(v) => set("strana", v)} /></div>
      <div><span className={POPISEK}>Druh zranění *</span><Cipy nazev="Druh zranění" volby={DRUHY} hodnota={z.zraneni_druh} onChange={(v) => set("zraneni_druh", v)} /></div>
      <Pole label="Rozsah, podrobnosti"><input className="field w-full" value={z.zraneni_popis || ""} onChange={(e) => set("zraneni_popis", e.target.value)} placeholder="Např. střední bolest, otok" /></Pole>
      <Pole label="První pomoc, kdo ji poskytl" povinne><input className="field w-full" value={z.prvni_pomoc || ""} onChange={(e) => set("prvni_pomoc", e.target.value)} placeholder="Např. chlazení, klid, Petr Janata" /></Pole>
      <div><span className={POPISEK}>Záchranka *</span><Cipy nazev="Záchranka" volby={ZACHRANKA} hodnota={z.zachranka} onChange={(v) => set("zachranka", v)} /></div>
      <div><span className={POPISEK}>Lékař *</span><Cipy nazev="Lékař" volby={LEKAR} hodnota={z.lekar} onChange={(v) => set("lekar", v)} /></div>
      <div>
        <span className={POPISEK}>Fotky a dokumenty</span>
        {prilohy.length > 0 && <div className="mb-2 flex flex-wrap gap-2">{prilohy.map((p) => <span key={p.key} className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700"><Camera className="h-4 w-4" />{p.filename}</span>)}</div>}
        <label className="btn btn-secondary w-full cursor-pointer"><Camera className="h-5 w-5" /> Vyfotit nebo přidat dokument
          <input type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif,application/pdf" className="sr-only" onChange={nahrat} disabled={busy} />
        </label>
      </div>
    </div>
  );

  const svedci = z.svedci || [];
  const setSvedek = (i, k, v) => set("svedci", svedci.map((s, j) => (j === i ? { ...s, [k]: v } : s)));
  const krok4 = (
    <div className="glass space-y-4 p-4">
      {svedci.length === 0 && <div className="text-sm text-slate-500">Svědky přidej, pokud u toho někdo byl.</div>}
      {svedci.map((s, i) => (
        <div key={i} className="space-y-3 rounded-xl border border-slate-200 bg-white p-3">
          <div className="flex items-center justify-between"><span className="text-[13px] font-semibold text-slate-500">Svědek {i + 1}</span>
            <button type="button" className="btn-ikona" aria-label={`Odebrat svědka ${i + 1}`} title="Odebrat" onClick={() => set("svedci", svedci.filter((_, j) => j !== i))}><Trash2 className="h-5 w-5" /></button>
          </div>
          <Pole label="Jméno a příjmení" povinne><input className="field w-full" value={s.jmeno || ""} onChange={(e) => setSvedek(i, "jmeno", e.target.value)} /></Pole>
          <Pole label="Telefon"><input className="field w-full" inputMode="tel" value={s.telefon || ""} onChange={(e) => setSvedek(i, "telefon", e.target.value)} /></Pole>
          <Pole label="E-mail"><input className="field w-full" type="email" inputMode="email" value={s.email || ""} onChange={(e) => setSvedek(i, "email", e.target.value)} /></Pole>
          <div className="rounded-xl bg-amber-50 px-3 py-2 text-[13px] font-semibold text-amber-700">Vyplň aspoň telefon, nebo e-mail.</div>
        </div>
      ))}
      <button type="button" className="btn btn-secondary w-full" onClick={() => set("svedci", [...svedci, { jmeno: "", telefon: "", email: "" }])}><Plus className="h-5 w-5" /> Přidat svědka</button>
      <Pole label="Opatření proti opakování"><textarea className="field w-full" rows={2} value={z.opatreni || ""} onChange={(e) => set("opatreni", e.target.value)} placeholder="Např. oprava stroje, úprava postupu" /></Pole>
    </div>
  );

  const krok5 = (
    <div className="glass space-y-4 p-4">
      <div className="space-y-2 text-[14px] text-slate-800">
        <div><b>{TYPY_TRENINKU.find((t) => t.key === z.typ_treninku)?.label}{z.bootcamp_nazev ? `, ${z.bootcamp_nazev}` : ""}</b>, {fmtDatum(z.datum)} v {z.cas}, {z.misto}</div>
        <div>Zraněný: <b className="demo-sensitive">{z.zraneny_jmeno}</b>, {z.zraneny_telefon}</div>
        <div>{[z.zraneni_druh, z.cast_tela, z.strana && z.strana !== "Neurčeno" ? z.strana.toLowerCase() : null].filter(Boolean).join(", ")}{z.zraneni_popis ? `, ${z.zraneni_popis}` : ""}</div>
        <div>Co se dělo: {z.cinnost}</div>
        <div>Příčina: {z.pricina}</div>
        <div>První pomoc: {z.prvni_pomoc}. Záchranka: {String(z.zachranka || "").toLowerCase()}. Lékař: {String(z.lekar || "").toLowerCase()}.</div>
        {svedci.length > 0 && <div>Svědci: {svedci.map((s) => s.jmeno).join(", ")}</div>}
      </div>
      {!podpis && stav === "koncept" && (
        <div className="flex items-start gap-2 rounded-xl bg-brand-50 px-3 py-2.5 text-[13px] font-semibold text-brand-deep">
          <MessageSquare className="mt-0.5 h-4 w-4 shrink-0" />
          <span>Po kliknutí ti přijde SMS s kódem. Zraněnému pak přijde SMS s odkazem, kde záznam potvrdí.</span>
        </div>
      )}
      {podpis?.kod_odeslan && (
        <Pole label={`Kód z SMS (číslo končící ${podpis.telefon_konec})`} povinne>
          <input className="field w-full text-center text-xl font-bold tracking-widest" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={kod} onChange={(e) => setKod(e.target.value.replace(/\D/g, ""))} />
        </Pole>
      )}
      {podpis?.bez_telefonu && (
        <div className="rounded-xl bg-amber-50 px-3 py-2.5 text-[13px] font-semibold text-amber-700">V profilu nemáš telefon, záznam podepíšeš potvrzením v aplikaci. Zapíše se to do historie.</div>
      )}
    </div>
  );

  const posledni = krok === KROKY.length - 1;
  return (
    <Stranka sirka="uzka">
      <GradientHeader title={id ? `Úraz č. ${id}` : "Zapsat úraz"} icon={Bandage} />
      <div className="glass p-4">
        <div className="flex items-center justify-between"><span className="text-[12px] font-bold uppercase tracking-wider text-slate-500">Krok {krok + 1} z {KROKY.length}</span>{id && <span className="text-[12px] font-semibold text-slate-500">uloženo jako koncept</span>}</div>
        <div className="mt-1 font-serif text-xl font-bold text-slate-900">{KROKY[krok]}</div>
        <div className="mt-3 flex gap-1.5">{KROKY.map((_, i) => <div key={i} className={`h-2 flex-1 rounded-full ${i <= krok ? "bg-brand" : "bg-slate-100"}`} />)}</div>
      </div>
      {[krok1, krok2, krok3, krok4, krok5][krok]}
      {chyba && <div className="rounded-xl bg-rose-50 px-3 py-2.5 text-[13px] font-semibold text-rose-700" role="alert">{chyba}</div>}
      <div className="flex gap-2">
        <button type="button" className="btn btn-secondary flex-1" onClick={zpet} disabled={busy}>Zpět</button>
        {!posledni && <button type="button" className="btn btn-primary flex-1" onClick={dalsi} disabled={busy}>{busy ? "Ukládám…" : "Další"}</button>}
        {posledni && stav !== "koncept" && <button type="button" className="btn btn-primary flex-1" disabled={busy} onClick={async () => { setBusy(true); setChyba(""); try { await ulozit(); toast("Změny uloženy.", "success"); navigate(`${base}/${id}`, { replace: true }); } catch (e) { setChyba(e.message); } finally { setBusy(false); } }}>{busy ? "Ukládám…" : "Uložit změny"}</button>}
        {posledni && stav === "koncept" && !podpis && <button type="button" className="btn btn-primary flex-1" onClick={podepsatStart} disabled={busy}>{busy ? "Posílám…" : "Podepsat přes SMS"}</button>}
        {posledni && podpis?.kod_odeslan && <button type="button" className="btn btn-primary flex-1" onClick={podepsatOverit} disabled={busy || kod.length !== 6}>{busy ? "Ověřuji…" : "Potvrdit kód"}</button>}
        {posledni && podpis?.bez_telefonu && <button type="button" className="btn btn-primary flex-1" onClick={podepsatOverit} disabled={busy}>{busy ? "Podepisuji…" : "Podepsat v aplikaci"}</button>}
      </div>
      {posledni && podpis?.kod_odeslan && <button type="button" className="btn btn-secondary w-full" onClick={podepsatStart} disabled={busy}>Poslat kód znovu</button>}
    </Stranka>
  );
}
